import {
  AttributeBox,
  Dropdown,
  DropdownWidth,
  MessageBanner,
  SegmentedControl,
  theme,
  typographyRem,
} from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useContext, useEffect, useState } from 'react'

import { numbersToLimitOptions } from '../../../../constants'
import useDriverQueries from '../../../../hooks/useDriverQueries'
import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { AccessibleFormWrapper } from '../../../AccessibleFormWrapper'
import { ToolTip } from '../../../ToolTip'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { YourTheir, YouThey } from '../../utils/driverCopyChange'
import { handleDropdownState } from '../../utils/handleDropdownState'
import { handlePrefilledDropdown } from '../../utils/handlePrefilledDropdown'
import { IntroductoryNcdModal } from './IntroductoryNcdModal'
import { IntroductoryNcdModalStyle } from './IntroductoryNcdModal/IntroductoryNcdModalStyle'
import { handleNcdAttributeValue } from './IntroductoryNcdModal/utils/handleNcdAttributeValue'
import { LicenceHelperStyle } from './LicenceHelperStyle'
const { h4: H4, h5: H5, paragraphBody: ParagraphBody } = typographyRem
dayjs.extend(utc)

export const DriverDriving = () => {
  const quote = useQuote()
  const {
    state: { isMobile, selectedDriverIndex, showModal },
    handleShowModal,
  } = useContext(ProviderContext)
  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()
  const {
    updatedDriverDetails,
    setUpdatedDriverDetails,
    updatedAdditionalDriver,
    setUpdatedAdditionalDriver,
    validateAndSave,
  } = useDriverPageContext()
  const { licenceData } = useDriverQueries()

  const additionalDrivers = quote?.details?.additional_drivers || []
  const selectedDriver = !selectedDriverIndex ? quote?.details?.proposer : additionalDrivers[selectedDriverIndex - 1]

  const [licence, setLicence] = useState<string>()
  const [licenceDate, setLicenceDate] = useState<string>('')
  const [ncd, setNCD] = useState<number | undefined | null>(quote?.details?.proposer?.ncd)
  const [drivesOtherVehicle, setDrivesOtherVehicle] = useState<boolean | undefined | null>(
    quote?.details?.proposer?.drives_other_vehicle,
  )
  const [ncdModalYears, setNcdModalYears] = useState<number | null>(quote?.details?.proposer?.other_vehicle_ncd || null)
  const [ncdModalOption, setNcdModalOption] = useState<string>(
    quote?.details?.proposer?.other_vehicle_relationship || '',
  )

  useEffect(() => {
    setLicence(selectedDriver?.licence_type || '')
    setLicenceDate(selectedDriver?.licence_issued_date || '')

    updateValidInputs({ field: 'licenceType', value: !!selectedDriver?.licence_type, index: selectedDriverIndex })
    updateValidInputs({
      field: 'licenceIssuedDate',
      value: !!selectedDriver?.licence_issued_date,
      index: selectedDriverIndex,
    })
  }, [selectedDriverIndex])

  const [licenceDateError, setLicenceDateError] = useState<string>('')

  const licenceError = showErrorBanner && !validInputs[selectedDriverIndex]?.licenceType ? notAnsweredMessage : ''
  const licenceDatePageError =
    showErrorBanner && !validInputs[selectedDriverIndex]?.licenceIssuedDate ? notAnsweredMessage : ''
  const ncdError = showErrorBanner && !validInputs[selectedDriverIndex]?.ncd ? notAnsweredMessage : ''
  const drivesOtherVehicleError =
    showErrorBanner && !validInputs[selectedDriverIndex]?.drivesOtherVehicle ? notAnsweredMessage : ''

  const handleLicenceType = (value: string) => {
    setLicence(value)
    DataLayer.customEvent({
      eventName: 'license type',
      section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
      eventType: 'form tracking',
    })
    !selectedDriverIndex
      ? setUpdatedDriverDetails({ ...updatedDriverDetails, licenceType: value })
      : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, licence_type: value })
  }

  const handleLicenceDateErrors = (years: number, date: string) => {
    const primaryDriverDob = updatedDriverDetails.dob || quote?.details?.proposer?.dob
    const driverDob = !selectedDriverIndex ? primaryDriverDob : updatedAdditionalDriver.dob

    const age = dayjs().diff(driverDob, 'year')
    if (!age) {
      setLicenceDateError('Please provide a valid date of birth before completing this section')
      updateValidInputs({ field: 'licenceIssuedDate', value: false, index: selectedDriverIndex })
    } else if (age - years < 17) {
      setLicenceDateError(
        `You must be 17 to get your driving licence. You don't look a day over ${age} so this can't be right.`,
      )
      updateValidInputs({ field: 'licenceIssuedDate', value: false, index: selectedDriverIndex })
    } else {
      setLicenceDateError('')
      updateValidInputs({ field: 'licenceIssuedDate', value: true, index: selectedDriverIndex })
      return true
    }
    return false
  }

  const handleLicenceYearsValid = (years: number, date: string) => {
    const valid = handleLicenceDateErrors(years, date)
    if (!selectedDriverIndex && date === quote?.details?.proposer?.licence_issued_date) return
    if (valid) {
      !selectedDriverIndex
        ? setUpdatedDriverDetails({ ...updatedDriverDetails, licenceIssuedDate: date })
        : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, licence_issued_date: date })
    }
  }

  useEffect(() => {
    if (licenceDate && (updatedDriverDetails?.dob || updatedAdditionalDriver?.dob)) {
      handleLicenceDateErrors(dayjs().diff(licenceDate, 'year'), licenceDate)
    }
  }, [updatedDriverDetails?.dob || updatedAdditionalDriver?.dob])

  const handleLicenceDate = (years: number) => {
    DataLayer.customEvent({
      eventName: 'licence years held',
      section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
      eventType: 'form tracking',
    })
    const date = dayjs().subtract(years, 'y').toISOString()
    setLicenceDate(date)
  }

  const handlePreviousLicence = (date: string) => {
    if (!date) return
    const yearsFromNow = dayjs().diff(date, 'year')
    if (yearsFromNow >= 31) return '31+'
    return yearsFromNow.toString()
  }

  useEffect(() => {
    const years = handlePreviousLicence(licenceDate) || ''
    if (years) {
      handleLicenceYearsValid(parseInt(years), licenceDate)
    }
  }, [licenceDate])

  const handleNCD = (value: number) => {
    setNCD(value)
    DataLayer.customEvent({
      eventName: 'ncd',
      section: 'driver',
      eventType: 'form tracking',
    })
    setDrivesOtherVehicle(null)
    setNcdModalYears(null)
    setNcdModalOption('')
    setUpdatedDriverDetails({
      ...updatedDriverDetails,
      ncd: value,
      drivesOtherVehicle: false,
      otherVehicleRelationship: 'N/A',
      otherVehicleNcd: 0,
      introNcdAwarded: 0,
    })
  }

  const handleNcdReset = () => {
    setNcdModalYears(null)
    setNcdModalOption('')
    setDrivesOtherVehicle(false)
    setUpdatedDriverDetails({
      ...updatedDriverDetails,
      drivesOtherVehicle: false,
      otherVehicleRelationship: 'N/A',
      otherVehicleNcd: 0,
      introNcdAwarded: 0,
    })
  }

  const handleDrivesOtherCar = (value: boolean) => {
    if (value) {
      setDrivesOtherVehicle(value)
      handleShowModal('introductory-ncd-modal-step-1')
      setUpdatedDriverDetails({ ...updatedDriverDetails, drivesOtherVehicle: value })
    } else {
      handleNcdReset()
    }
  }

  const handleNcdModalClose = (selected: string, claimFreeYears: number | null) => {
    setNcdModalOption(selected)
    setNcdModalYears(claimFreeYears)
    setUpdatedDriverDetails({
      ...updatedDriverDetails,
      otherVehicleRelationship: selected,
      otherVehicleNcd: claimFreeYears || 0,
      introNcdAwarded: (claimFreeYears && claimFreeYears >= 2 ? 2 : 1) || 0,
    })
  }

  useEffect(() => {
    updateValidInputs({ field: 'licenceType', value: !!licence, index: selectedDriverIndex })
  }, [licence])
  useEffect(() => {
    updateValidInputs({
      field: 'licenceIssuedDate',
      value: !!licenceDate && !licenceDateError,
      index: selectedDriverIndex,
    })
  }, [licenceDate, licenceDateError])
  useEffect(() => {
    updateValidInputs({ field: 'ncd', value: typeof ncd === 'number', index: selectedDriverIndex })
    updateValidInputs({ field: 'drivesOtherVehicle', value: ncd !== 0, index: selectedDriverIndex })
  }, [ncd])
  useEffect(() => {
    ncd === 0 &&
      updateValidInputs({
        field: 'drivesOtherVehicle',
        value: typeof drivesOtherVehicle === 'boolean',
        index: selectedDriverIndex,
      })
  }, [drivesOtherVehicle])

  return (
    <>
      <GenericSectionStyle>
        <H4 className="title">{`${YourTheir()} driving.`}</H4>
        <div className="generic-wrapper">
          <AccessibleFormWrapper onSubmit={validateAndSave}>
            {licenceData && (
              <div className="question-container" id="licenceType">
                <div className="question-text-container">
                  <H5>{`What kind of driving licence do ${YouThey()} have?`}</H5>
                </div>
                <div className="input-info-container">
                  <Dropdown
                    handleDropdownChange={option => {
                      handleLicenceType(option.value)
                    }}
                    isMobile={isMobile}
                    options={licenceData?.licenceType || []}
                    preFilled={handlePrefilledDropdown(licenceData?.licenceType || [], licence)}
                    states={handleDropdownState(licence, licenceError)}
                    errorMessage={licenceError}
                    width={DropdownWidth.Large}
                    className="dropdown-large-with-icon dropdown"
                    placeholder="Select"
                    dataCy="licence-type-dropdown-list"
                    tabFocusTrap={showModal ? -1 : 0}
                    ariaLiveOnFocus
                  />
                  <ToolTip
                    text={`If the type of licence ${YouThey()} have isn't shown here then unfortunately we won't be able to insure you at the moment.`}
                    dataCy="licence-type-button"
                    className="tool-tip tip-top-small"
                    tabFocusTrap={showModal ? -1 : 0}
                  />
                </div>
              </div>
            )}
            <div className="question-container" id="licenceIssuedDate">
              <div className="question-text-container">
                <H5>{`How many years have ${YouThey()} held a full driving licence?`}</H5>
              </div>
              <div className="input-info-container">
                <Dropdown
                  handleDropdownChange={option => {
                    handleLicenceDate(option.value)
                  }}
                  isMobile={isMobile}
                  options={numbersToLimitOptions(31)}
                  preFilled={handlePreviousLicence(licenceDate)}
                  states={handleDropdownState(licenceDate, licenceDateError || licenceDatePageError)}
                  errorMessage={licenceDateError || licenceDatePageError}
                  width={DropdownWidth.Large}
                  className="dropdown-small-with-icon dropdown-bellow"
                  placeholder="Select"
                  dataCy="licence-date-dropdown-list"
                  tabFocusTrap={showModal ? -1 : 0}
                  ariaLiveOnFocus
                />
                <ToolTip
                  text={
                    <LicenceHelperStyle>
                      <div className="helper-text">
                        {`You can find the original issue date of ${YourTheir()} driving licence on the back of ${YourTheir()} photocard, in the row titled 'B' next to the small car icon.`}
                      </div>
                      <img className="helper-image" alt="" src={`/v2/assets/licence.png`} />
                    </LicenceHelperStyle>
                  }
                  dataCy="licence-info-button"
                  className="tool-tip tip-top-small"
                  tabFocusTrap={showModal ? -1 : 0}
                />
              </div>
            </div>
            {!selectedDriverIndex && (
              <>
                <div className="question-container" id="ncd">
                  <div className="question-text-container">
                    <H5>How many years No Claims Discount did your previous insurer give you?</H5>
                    <ParagraphBody className="question-info">
                      This needs to be on a personal car in the UK in the last 24 months.
                    </ParagraphBody>
                  </div>
                  <div className="input-info-container">
                    <Dropdown
                      handleDropdownChange={option => {
                        handleNCD(option.value)
                      }}
                      isMobile={isMobile}
                      options={numbersToLimitOptions(21)}
                      preFilled={handlePrefilledDropdown(numbersToLimitOptions(21), ncd)}
                      states={handleDropdownState(ncd, ncdError)}
                      errorMessage={ncdError}
                      width={DropdownWidth.Large}
                      className="dropdown-small-with-icon dropdown-bottom"
                      placeholder="Select"
                      dataCy="ncd-date-dropdown-list"
                      tabFocusTrap={showModal ? -1 : 0}
                      ariaLiveOnFocus
                    />
                    <ToolTip
                      text="This can be found on your insurance renewal documents. You can only earn and use No Claims Discount on one car at a time, so if you're already using it on another car, answer '0' and you can add those details separately. We can only accept No Claims Discount earned in the UK on a personal car policy and issued in the last 24 months. Unfortunately, we don't accept commercial No Claims Discounts."
                      className="tool-tip tip-top-small"
                      dataCy="ncd-info-button"
                      tabFocusTrap={showModal ? -1 : 0}
                    />
                  </div>
                </div>
                {ncd === 0 && (
                  <div className="question-container" id="drivesOtherVehicle">
                    <div className="question-text-container">
                      <H5>Have you driven any other cars in the past year?</H5>
                    </div>
                    <div className="input-info-container">
                      <SegmentedControl
                        options={[
                          { label: 'yes', value: true },
                          { label: 'no', value: false },
                        ]}
                        name="drives-other-vehicle"
                        preFilledValue={drivesOtherVehicle}
                        error={drivesOtherVehicleError}
                        onClick={value => typeof value === 'boolean' && handleDrivesOtherCar(value)}
                        dataCy="drives-other-vehicle-toggle"
                        tabFocusTrap={showModal ? -1 : 0}
                        ariaLiveOnFocus
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </AccessibleFormWrapper>
          {!selectedDriverIndex && (
            <>
              {drivesOtherVehicle && (
                <IntroductoryNcdModal
                  onClick={(selected: string, claimFreeYears: number | null) =>
                    handleNcdModalClose(selected, claimFreeYears)
                  }
                  ncdModalYears={ncdModalYears}
                  ncdModalOption={ncdModalOption}
                />
              )}
              {ncdModalOption && ncdModalOption !== 'N/A' && (
                <IntroductoryNcdModalStyle>
                  {!!ncdModalYears && (
                    <div className="ncd-bonus-container">
                      <MessageBanner
                        icon="IconNcdRosette"
                        ncdYears={ncdModalYears >= 2 ? 2 : 1}
                        text={
                          <>
                            <ParagraphBody className="ncd-discount-text-bold">
                              {`Great! ${ncdModalYears >= 2 ? 2 : 1} years introductory No Claims Discount unlocked.`}
                            </ParagraphBody>
                            <ParagraphBody className="ncd-discount-text">
                              We’ll add this to your By Miles policy and protect it as standard.
                            </ParagraphBody>
                          </>
                        }
                        borderColor={theme.tints.darkMint}
                      />
                    </div>
                  )}
                  <div className="additonal-wrapper">
                    <AttributeBox
                      title="NCD modal option"
                      className="attribute-box"
                      attributes={
                        !!ncdModalYears
                          ? [
                              { label: 'Claim-free years', value: ncdModalYears?.toString() || '0' },
                              { label: 'Earned on', value: handleNcdAttributeValue(ncdModalOption) },
                            ]
                          : [{ label: 'Claim-free years', value: ncdModalYears?.toString() || '0' }]
                      }
                      onEdit={() => handleShowModal('introductory-ncd-modal-step-2')}
                      onRemove={() => handleNcdReset()}
                      tabFocusTrap={showModal ? -1 : 0}
                    />
                  </div>
                  {!!ncdModalYears && (
                    <div className="ncd-banner">
                      <MessageBanner
                        text="We’ll be in touch to ask for proof of your previous policy and/or claims history."
                        backgroundColor={theme.tints.lightAubergine}
                        borderColor={theme.primaryColours.aubergine.main}
                      />
                    </div>
                  )}
                </IntroductoryNcdModalStyle>
              )}
            </>
          )}
        </div>
      </GenericSectionStyle>
    </>
  )
}
